import { useMemo } from 'react';
import { useLinkStore } from '@/stores/linkStore';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CheckSquare,
  AlertTriangle,
  Shield,
  AlertCircle,
  Briefcase,
  FileText,
  Link2,
} from 'lucide-react';
import type { EntityType } from '@/types/link';

interface LinkIndicatorProps {
  entityType: EntityType;
  entityId: string;
  compact?: boolean;
  onClick?: () => void;
}

interface LinkCount {
  type: EntityType;
  count: number;
}

const LINK_ICONS: Partial<Record<EntityType, { icon: React.ElementType; color: string; label: string }>> = {
  task: { icon: CheckSquare, color: 'text-blue-500', label: 'Tasks' },
  risk: { icon: AlertTriangle, color: 'text-red-500', label: 'Risks' },
  control: { icon: Shield, color: 'text-green-500', label: 'Controls' },
  incident: { icon: AlertCircle, color: 'text-orange-500', label: 'Incidents' },
  supplier: { icon: Briefcase, color: 'text-purple-500', label: 'Suppliers' },
  evidence: { icon: FileText, color: 'text-slate-500', label: 'Evidence' },
};

export function LinkIndicator({ entityType, entityId, compact = false, onClick }: LinkIndicatorProps) {
  const { links } = useLinkStore();

  const linkCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    
    links.forEach(link => {
      if (
        (link.source_type === entityType && link.source_id === entityId) ||
        (link.target_type === entityType && link.target_id === entityId)
      ) {
        const linkedType = link.source_type === entityType ? link.target_type : link.source_type;
        counts[linkedType] = (counts[linkedType] || 0) + 1;
      }
    });

    return Object.entries(counts)
      .filter(([type]) => LINK_ICONS[type as EntityType])
      .map(([type, count]) => ({ type: type as EntityType, count })) as LinkCount[];
  }, [links, entityType, entityId]);

  const totalLinks = linkCounts.reduce((sum, lc) => sum + lc.count, 0);

  if (totalLinks === 0) return null;

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className="cursor-pointer gap-1 text-xs px-1.5 py-0"
              onClick={onClick}
            >
              <Link2 className="h-3 w-3" />
              {totalLinks}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              <p className="font-medium text-xs">Linked Entities</p>
              {linkCounts.map(({ type, count }) => {
                const config = LINK_ICONS[type];
                if (!config) return null;
                const Icon = config.icon;
                return (
                  <div key={type} className="flex items-center gap-1.5 text-xs">
                    <Icon className={`h-3 w-3 ${config.color}`} />
                    <span>{count} {config.label}</span>
                  </div>
                );
              })}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap" onClick={onClick}>
      {linkCounts.map(({ type, count }) => {
        const config = LINK_ICONS[type];
        if (!config) return null;
        const Icon = config.icon;
        return (
          <TooltipProvider key={type}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="gap-0.5 text-xs px-1.5 py-0 cursor-default">
                  <Icon className={`h-3 w-3 ${config.color}`} />
                  <span>{count}</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <span>{count} {config.label}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}
